/**
 * @desc 文本转化成 canvas 方法
 */
const TEXT_BASE_LINE = 'middle';
const OFFSET_Y = 0.08; // 文本 y轴 偏移量
const TOLERATE = 4;

const FJTextToCanvasUtil = {
    /**
     * @desc 将文本渲染到canvas上
     * @param {[string]} textList
     * @param {HTMLCanvasElement} canvas
     * @param {{}} options
     */
    drawToCanvas: function (textList, canvas, options) {
        const {
            fontFamily,
            fontSize,
            fontWeight,
            fontStyle,
            lineHeight,
            textAlign,
            letterSpacing = 0,
            outOffset = 0, // 渲染画布的外扩偏移
            opacity = 1,
        } = options;

        // 最大宽度
        let maxWidth = options.maxWidth;
        if (!maxWidth) {
            maxWidth = canvas.width;
        }

        const ctx = canvas.getContext('2d');
        ctx.save();

        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.textAlign = textAlign;
        ctx.textBaseline = TEXT_BASE_LINE;
        ctx.globalAlpha = opacity;

        const renderLineHeight = fontSize * lineHeight;

        let y = renderLineHeight / 2 + OFFSET_Y * renderLineHeight + outOffset;
        let x = 0 + outOffset;

        const __drawText = (ctx, text, x, y) => {
            // 每行最少保留一个空格， 防止没有字符导致的元素高度/宽度计算错误
            if (text === '') text = ' ';
            const textWidth = ctx.measureText(text).width + letterSpacing * text.length;
            switch (textAlign) {
                case 'left': {
                    x += 0;
                    break;
                }
                case 'right': {
                    if (letterSpacing === 0) {
                        x += maxWidth;
                    } else {
                        x += maxWidth - textWidth;
                    }
                    break;
                }
                default: {
                    if (letterSpacing === 0) {
                        x += maxWidth / 2;
                    } else {
                        x += maxWidth / 2 - textWidth / 2;
                    }
                }
            }
            this.drawText(ctx, text, x, y, letterSpacing);
        };
        // 按行分割的字符串数组
        let textLineList = [];

        textList.forEach(text => {
            text = text
                .replace(/\s/g, ' ')
                .replace(/[\b\f\n\r\t]/g, '')
                .replace(/\u0003/g, ' ')
                .replace(/\u001f/g, '')
                .replace(/[\u0000-\u0008]/g, '');
            const words = text.split(' ');
            if (maxWidth) {
                const textLines = this._textSplitInWrap(words, ctx, { maxWidth, letterSpacing });

                textLineList = [...textLineList, ...textLines];
            } else {
                textLineList.push(text);
            }
        });

        textLineList.forEach((textLine, index) => {
            __drawText(ctx, textLine, x, y);
            y += renderLineHeight;
        });

        ctx.restore();
    },

    /**
     * @desc 渲染一行文字
     * @param {CanvasRenderingContext2D} ctx
     * @param {string} text  需要渲染的字符串
     * @param {number} x  渲染位置
     * @param {number} y  渲染位置
     * @param {number} letterSpacing 字间距
     */
    drawText(ctx, text, x, y, letterSpacing) {
        ctx.save();
        const characters = Array.from(text);
        if (letterSpacing) {
            ctx.textAlign = 'left';
            let currentPosition = x + letterSpacing / 2;
            // 逐个绘制字符
            characters.forEach((char, index) => {
                ctx.fillText(char, currentPosition, y);
                currentPosition += ctx.measureText(char).width + letterSpacing;
            });
        } else {
            ctx.fillText(text, x, y);
        }

        ctx.restore();
    },

    /**
     * @desc 计算文本渲染尺寸
     * @param {[string]} textList
     * @param {{}} options
     * @param {{}} options.baseCss
     * @param {number | null} options.maxWidth 最大宽度
     * @param {number | null} options.fixWidth 固定宽度
     */
    calcTextDrawSize(textList, options) {
        const { baseCss, maxWidth, fixWidth } = options;
        const { fontFamily, fontSize, fontWeight, fontStyle, lineHeight, textAlign, letterSpacing = 0 } = baseCss;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.textAlign = textAlign;
        ctx.textBaseline = TEXT_BASE_LINE;

        const renderLineHeight = fontSize * lineHeight;
        let lineNum = 0;
        let width = 0;

        if (fixWidth) {
            width = fixWidth;
            textList.forEach(text => {
                text = text
                    .replace(/\s/g, ' ')
                    .replace(/[\b\f\n\r\t]/g, '')
                    .replace(/\u0003/g, ' ')
                    .replace(/\u001f/g, '')
                    .replace(/[\u0000-\u0008]/g, '');
                const words = text.split(' ');
                const textLines = this._textSplitInWrap(words, ctx, { maxWidth: fixWidth, letterSpacing });

                lineNum += textLines.length;
            });
        } else {
            if (maxWidth) {
                textList.forEach(text => {
                    text = text
                        .replace(/\s/g, ' ')
                        .replace(/[\b\f\n\r\t]/g, '')
                        .replace(/\u0003/g, ' ')
                        .replace(/\u001f/g, '')
                        .replace(/[\u0000-\u0008]/g, '');
                    const words = text.split(' ');
                    const textLines = this._textSplitInWrap(words, ctx, { maxWidth, letterSpacing });

                    textLines.forEach(textLine => {
                        const lineWidth = ctx.measureText(textLine).width + letterSpacing * textLine.length;
                        width = Math.max(lineWidth, width);
                    });

                    lineNum += textLines.length;
                });
            } else {
                for (let text of textList) {
                    // 每行最少保留一个空格， 防止没有字符导致的元素高度/宽度计算错误
                    if (text === '') {
                        text = ' ';
                    }
                    lineNum++;
                    const lineWidth = ctx.measureText(text).width + letterSpacing * text.length;
                    width = Math.max(lineWidth, width);
                }
            }
        }

        if (width === 0) {
            width = ctx.measureText(' ').width + letterSpacing;
        }
        if (lineNum === 0) {
            lineNum = 1;
        }

        return {
            width: width,
            height: lineNum * renderLineHeight,
        };
    },

    /**
     * @desc 按css换行规则拆分文本
     * @param {[string]} words
     * @param {CanvasRenderingContext2D} ctx
     * @param {{}} options
     * @param {number} options.maxWidth 必须
     */
    _textSplitInWrap(words, ctx, options = {}) {
        const { maxWidth, letterSpacing = 0 } = options;
        let line = '';
        let lines = [];

        const __getLineWidth = text => {
            return ctx.measureText(text).width + letterSpacing * text.length;
        };

        const __splitTextOfCharts = text => {
            const inLines = [];
            let outText = '';
            let line = '';
            for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const lineWidth = __getLineWidth(line + char);
                if (lineWidth > maxWidth + TOLERATE) {
                    if (line !== '') {
                        inLines.push(line);
                        outText = text.slice(i);
                    } else {
                        inLines.push(char);
                        outText = text.slice(i + 1);
                    }
                    break;
                } else {
                    line += char;
                }
            }
            return { inLines, outText };
        };

        let wordsQueue = [];
        for (let i = words.length - 1; i >= 0; i--) wordsQueue.push(words[i]);

        while (wordsQueue.length > 0) {
            const word = wordsQueue.pop();
            const testLine = line + word;
            const lineWidth = __getLineWidth(testLine);

            if (lineWidth > maxWidth + TOLERATE) {
                if (line !== '') {
                    lines.push(line);
                    wordsQueue.push(word);
                    line = '';
                } else {
                    // 单个单词超出，拆分单词换行
                    const { inLines, outText } = __splitTextOfCharts(word);
                    lines = [...lines, ...inLines];
                    if (outText !== '') {
                        wordsQueue.push(outText);
                    }
                    line = '';
                }
            } else {
                if (wordsQueue.length === 0) {
                    line += word;
                } else {
                    line += word + ' ';
                }
            }
        }

        if (line !== '') lines.push(line);

        // 最少保留一个空格
        if (lines.length === 0) lines.push(' ');
        return lines;
    },

    /**
     * @desc 计算文字每行的四个顶点
     * @param {[]} textList
     * @param {{}} options
     * @returns
     */
    getCanvasLineTextPeakPoints(textList, options) {
        let { baseCss, maxWidth } = options;
        const { fontFamily, fontSize, fontWeight, fontStyle, lineHeight, textAlign, letterSpacing = 0 } = baseCss;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.textAlign = textAlign;
        ctx.textBaseline = TEXT_BASE_LINE;

        const renderLineHeight = fontSize * lineHeight;

        // 每行的顶点
        const rectPeakPoints = [];
        // 每行的宽
        const widthLines = [];

        if (maxWidth) {
            // 实际的换行列表
            const currentLineText = [];
            textList.forEach(text => {
                text = text
                    .replace(/\s/g, ' ')
                    .replace(/[\b\f\n\r\t]/g, '')
                    .replace(/\u0003/g, ' ')
                    .replace(/\u001f/g, '')
                    .replace(/[\u0000-\u0008]/g, '');
                const words = text.split(' ');
                const textLines = this._textSplitInWrap(words, ctx, { maxWidth, letterSpacing });
                currentLineText.push(...textLines);
            });

            if (currentLineText.length === 0) currentLineText.push(' ');
            for (let text of currentLineText) {
                const lineWidth = ctx.measureText(text).width + letterSpacing * text.length;
                widthLines.push(lineWidth);
            }
        } else {
            for (let text of textList) {
                if (text === '') {
                    text = ' ';
                    const lineWidth = ctx.measureText(text).width + letterSpacing;
                    maxWidth = Math.max(lineWidth, maxWidth);
                    widthLines.push(lineWidth);
                } else {
                    const lineWidth = ctx.measureText(text).width + letterSpacing * text.length;
                    maxWidth = Math.max(lineWidth, maxWidth);
                    widthLines.push(lineWidth);
                }
            }
        }

        for (let i = 0; i < widthLines.length; i++) {
            const width = widthLines[i];
            let leftUp = { x: 0, y: 0 },
                rightUp = { x: 0, y: 0 },
                rightDown = { x: 0, y: 0 },
                leftDown = { x: 0, y: 0 };

            switch (textAlign) {
                case 'left': {
                    leftUp = { x: 0, y: i * renderLineHeight };
                    rightUp = { x: width, y: i * renderLineHeight };
                    rightDown = { x: width, y: (i + 1) * renderLineHeight };
                    leftDown = { x: 0, y: (i + 1) * renderLineHeight };
                    break;
                }

                case 'right': {
                    leftUp = { x: maxWidth - width, y: i * renderLineHeight };
                    rightUp = { x: maxWidth, y: i * renderLineHeight };
                    rightDown = { x: maxWidth, y: (i + 1) * renderLineHeight };
                    leftDown = { x: maxWidth - width, y: (i + 1) * renderLineHeight };
                    break;
                }
                default: {
                    leftUp = { x: (maxWidth - width) / 2, y: i * renderLineHeight };
                    rightUp = { x: (maxWidth - width) / 2 + width, y: i * renderLineHeight };
                    rightDown = { x: (maxWidth - width) / 2 + width, y: (i + 1) * renderLineHeight };
                    leftDown = { x: (maxWidth - width) / 2, y: (i + 1) * renderLineHeight };
                    break;
                }
            }

            rectPeakPoints.push([leftUp, rightUp, rightDown, leftDown]);
        }

        return rectPeakPoints;
    },
};

export default FJTextToCanvasUtil;
